import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/layout/Layout';
import Chat from './pages/Chat';
import Models from './pages/Models';
import DataSources from './pages/DataSources';
import Dashboards from './pages/Dashboards';
import DashboardDetail from './pages/DashboardDetail';
import DataEnhancement from './pages/DataEnhancement';
import DataTransformation from './pages/DataTransformation';
import DataTransformationCreate from './pages/DataTransformationCreate';
import DataTransformationDetails from './pages/DataTransformationDetails';
import ModelCreation from './pages/ModelCreation';
import ModelDetails from './pages/ModelDetails';
import DatasetDetails from './pages/DatasetDetails';
import Usage from './pages/Usage';
import Settings from './pages/Settings';
import TopUpRequired from './pages/TopUpRequired';
import PublicModelView from './pages/PublicModelView';
import { AuthProvider } from './contexts/AuthContext';
import Login from './pages/Login';
import ResetPassword from './pages/ResetPassword';
import GoogleAuthCallback from './pages/GoogleAuthCallback';
import ProtectedRoute from './components/ProtectedRoute';
import { NotificationProvider } from './contexts/NotificationContext';
import NotificationContainer from './components/ui/NotificationContainer';
import { ThemeProvider } from './contexts/ThemeContext';
import { useFeatureFlags } from './hooks/useFeatureFlags';

function AppRoutes() {
  const { features, isLoading } = useFeatureFlags(['data_transformation']);

  // While loading features, you could show a loading spinner or just render all routes
  if (isLoading) {
    return <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="*" element={<div className="flex items-center justify-center h-screen">Loading...</div>} />
    </Routes>;
  }

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/auth/callback" element={<GoogleAuthCallback />} />
      <Route path="/top-up-required" element={<TopUpRequired />} />

      {/* Public shared model view (no auth required) */}
      <Route path="/share/:token" element={<PublicModelView />} />

      {/* Routes requiring auth and top-up */}
      <Route element={<ProtectedRoute />}>
        <Route element={<Layout />}>
          <Route index element={<Navigate to="/chat" replace />} />
          <Route path="/chat" element={<Chat />} />
          <Route path="/models" element={<Models />} />
          <Route path="/models/:modelId" element={<ModelDetails />} />
          <Route path="/models/create" element={<ModelCreation />} />
          <Route path="/data-sources" element={<DataSources />} />
          <Route path="/datasets/:uploadId" element={<DatasetDetails />} />
          <Route path="/dashboards" element={<Dashboards />} />
          <Route path="/dashboards/:dashboardId" element={<DashboardDetail />} />
          <Route path="/data-enhancement" element={<DataEnhancement />} />
          
          {/* Conditionally render data transformation routes */}
          {features.data_transformation && (
            <>
              <Route path="/data-transformation" element={<DataTransformation />} />
              <Route path="/data-transformation/create" element={<DataTransformationCreate />} />
              <Route path="/data-transformation/:id" element={<DataTransformationDetails />} />
            </>
          )}
          
          <Route path="/usage" element={<Usage />} />
          <Route path="/settings" element={<Settings />} />
        </Route>
      </Route>
      
      
      <Route path="*" element={<Navigate to="/chat" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <NotificationProvider>
        <ThemeProvider>
          <NotificationContainer />
          <AppRoutes />
        </ThemeProvider>
      </NotificationProvider>
    </AuthProvider>
  );
}

export default App;